import { FORM_ERROR } from 'final-form';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { ScaleFilterTypes, ScalesRespI } from '~src/interfaces/scale.interface';
import { ScheduleI } from '~src/interfaces/schedule.interface';

import { Topic } from '~src/interfaces/topic.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { fetchSchedulesOfSurvey } from '~src/modules/surveys/actions/schedulesActions';
import { getSurvey, updateSurvey } from '~src/modules/surveys/actions/surveyActions';
import {
  fetchTopicsForSurvey,
  FetchTopicsForSurveyParams,
  TopicsForSurvey,
} from '~src/modules/surveys/actions/topicActions';
import SchedulesContainer from '~src/modules/surveys/components/schedules/SchedulesContainer';
import { TopicValueI } from '~src/modules/surveys/components/topic_select/edit/TopicQuestionsForm';
import TopicSelect from '~src/modules/surveys/components/topic_select/edit/TopicSelect';
import SummarySelect from '~src/modules/surveys/components/topic_select/summary/SummarySelect';
import { NewSurveySubmitData, Survey, SurveyScopes } from '~src/modules/surveys/interfaces';
import {
  fetchAllQuestions,
  fetchTopicsList,
  FetchTopicsParams,
} from '~src/modules/topics/actions/topicActions';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import {ApiReduxResp, getLoggingContext, isSuccess, ThunkMWDispatch} from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { getLocationState } from '~src/utils';
import { LoadingContainer, PageContentContainerS } from './surveyDetails.sc';

import SurveyHeader from './SurveyHeader';

interface SearchStrObjI {
  page?: number;
  filterSubject?: ScaleFilterTypes;
}

type OwnProps = WithTranslation & RouteComponentProps<{ surveyId: string }>;

interface StateProps {
  surveyId: string;
  survey: Survey | null;
  loadingSurvey: boolean;
  loadingSchedules: boolean;
  loadingTopics: boolean;
  loadingTopicsList: boolean;
  schedules?: { [key: string]: ScheduleI };
}

interface DispatchProps {
  fetchAllQuestions: () => ApiReduxResp<unknown>;
  fetchScales: (params: SearchStrObjI) => ApiReduxResp<ScalesRespI>;
  getSurvey: (id: string) => ApiReduxResp<Survey>;
  fetchSchedulesOfSurvey: (id: string) => ApiReduxResp<ScheduleI[]>;
  fetchTopicsList: (params: FetchTopicsParams) => ApiReduxResp<Topic[]>;
  fetchTopicsForSurvey: (
    surveyId: string,
    params: FetchTopicsForSurveyParams,
  ) => ApiReduxResp<TopicsForSurvey>;
  updateSurvey: (id: string, formData: NewSurveySubmitData) => ApiReduxResp<Survey>;
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  questionCount: number;
  loadingAll: boolean;
}

class SurveyDetails extends React.Component<Props, State> {
  static defaultProps = {
    survey: null,
  };

  state = {
    questionCount: 0,
    loadingAll: true,
  };

  componentDidMount() {
    this.fetchingStuff();
  }

  fetchingStuff = () => {
    void this.props.getSurvey(this.props.surveyId).then(async (thunkAction) => {
      if (isSuccess(thunkAction)) {
        const survey = thunkAction.response.data;

        let includeString: FetchTopicsForSurveyParams['include'] = 'questions';
        if (survey.scope !== SurveyScopes.Admin) {
          includeString = 'questions,private';
        }
        // need to wait for all topics because
        // for topics of survey we map them with the whole list
        await this.props.fetchTopicsList({
          'filter.subject': survey.subject,
          per_page: 1000,
          include: includeString,
        });
        await Promise.all([
          this.props.fetchAllQuestions(),
          this.props.fetchSchedulesOfSurvey(this.props.surveyId),
          this.props.fetchTopicsForSurvey(this.props.surveyId, { include: includeString }),
          this.props.fetchScales({ page: 1 }),
        ]);
        this.setState({ questionCount: survey.questionCount });
      }
      this.setState({ loadingAll: false });
      window.scrollTo(0, 0);
    });
  };

  changeQuestionCount = (count: number) => {
    if (count !== this.state.questionCount) {
      this.setState({ questionCount: count });
    }
  };

  renderEditMode = () => (
    <TopicSelect
      changeQuestionCount={this.changeQuestionCount}
      survey={this.props.survey as Survey}
      surveyId={this.props.match.params.surveyId}
      saveSurvey={this.saveSurvey}
      questionCount={this.state.questionCount}
      isNewSurvey={
        !(this.props.schedules && Object.values(this.props.schedules).length > 0) &&
        this.state.questionCount <= 0
      }
    />
  );

  renderSummaryMode = () => (
    <>
      {this.props.loadingSurvey || !this.props.survey ? (
        <LoadingContainer>
          <LoadingSpinner alignCls="center-middle" />
        </LoadingContainer>
      ) : (
        <SummarySelect t={this.props.t} saveSurvey={this.saveSurvey} survey={this.props.survey} />
      )}
    </>
  );

  saveSurvey = (request: TopicValueI) => {
    return this.props
      .updateSurvey(request.surveyId, request.body)
      .then((response) => {
        if (!isSuccess(response)) {
          const { error } = response;
          return {
            surveyName: error.response ? error.response.data.error : 'UNKNOWN',
            [FORM_ERROR]: 'saving topics failed!',
          };
        } else {
          let includeString: FetchTopicsForSurveyParams['include'] = 'questions';
          if (response.response.data.scope !== SurveyScopes.Admin) {
            includeString = 'questions,private';
          }
          void this.props.fetchTopicsForSurvey(request.surveyId, { include: includeString });
        }
      })
      .catch((err) => {
        logger.info('Error saving survey topics', getLoggingContext(err, 'TopicAdd'));
      });
  };

  getTopicSelection = (): string => {
    if (
      (this.props.schedules && Object.values(this.props.schedules).length > 0) ||
      this.state.questionCount > 0
    ) {
      return 'summary';
    }
    return 'edit';
  };

  render() {
    const { t, survey } = this.props;
    const { collapsedTitleForm } = getLocationState();
    const isEditFormInitiallyVisible = typeof collapsedTitleForm === 'undefined';

    return this.state.loadingAll ? (
      <LoadingSpinner alignCls="center-middle" />
    ) : (
      survey && (
        <PageContentContainerS>
          <SurveyHeader
            survey={survey}
            isEditFormInitiallyVisible={isEditFormInitiallyVisible}
          />
          <Switch>
            <Route exact strict path={`${this.props.match.url}/edit`} render={this.renderEditMode} />
            <Route exact strict path={`${this.props.match.url}/summary`} render={this.renderSummaryMode} />
            <Redirect to={`${this.props.match.url}/${this.getTopicSelection()}`} />
          </Switch>
          <SchedulesContainer
            survey={survey}
            questionCount={this.state.questionCount}
            surveyId={this.props.match.params.surveyId}
          />
          <TextBtnPageFooter
            t={t}
            infoKey="x.surveys.list.footer.title"
            btnKey="x.surveys.list.footer.button"
            linkUrl={routes.contentTopics.topics}
          />
        </PageContentContainerS>
      )
    );
  }
}

const mapStateToProps = ({ surveyManagement, topics }: StoreI, ownProps: OwnProps): StateProps => ({
  surveyId: ownProps.match.params.surveyId,
  survey: surveyManagement.surveys.survey,
  schedules: surveyManagement.schedules.byId,
  loadingSurvey: surveyManagement.surveys.loadingSurvey,
  loadingSchedules: surveyManagement.schedules.loading,
  loadingTopics: topics.surveyTopics.loading,
  loadingTopicsList: topics.loading,
});

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  getSurvey: id => dispatch(getSurvey(id)),
  fetchSchedulesOfSurvey: id => dispatch(fetchSchedulesOfSurvey(id)),
  fetchTopicsList: params => dispatch(fetchTopicsList(params)),
  fetchTopicsForSurvey: (surveyId, params) => dispatch(fetchTopicsForSurvey(surveyId, params)),
  fetchScales: params => dispatch(fetchScales(params)),
  updateSurvey: (id, formData) => dispatch(updateSurvey(id, formData)),
  fetchAllQuestions: () => dispatch(fetchAllQuestions()),
});

const SurveyDetailWithTranslation = withTranslation()(SurveyDetails);
const SurveyDetailsSuperComponent = connect(mapStateToProps, mapDispatchToProps)(SurveyDetailWithTranslation);

export default SurveyDetailsSuperComponent;
